import { get, set } from "lodash";
import { ResponsiveElement } from "./ResponsiveElement";
import React from "react";
import { ElementType } from "@easy-email-pro-core/constants";
import { ResponsivePlugin } from "../PluginManager";
import { classnames } from "@easy-email-pro-core/utils/classnames";
import { mergeBlock } from "@easy-email-pro-core/utils/mergeBlock";
import { BlockManager } from "@easy-email-pro-core/utils/BlockManager";

export const HIDE_DESKTOP_BLOCK_CLASS_NAME = "hide-desktop-block";
export const HIDE_MOBILE_BLOCK_CLASS_NAME = "hide-mobile-block";
export const HIDE_DESKTOP_INLINE_BLOCK_CLASS_NAME = "hide-desktop-inline-block";
export const HIDE_MOBILE_INLINE_BLOCK_CLASS_NAME = "hide-mobile-inline-block";

// All custom blocks will eventually become basic blocks, so just use BasicType
const inlineBlockTypes = [
  ElementType.GROUP,
  ElementType.COLUMN,
  ElementType.NAVBAR_LINK,
] as string[];

export class Responsive extends ResponsivePlugin {
  generateResponsive: ResponsivePlugin["generateResponsive"] = (params) => {
    const {
      mode,
      displayMode,
      node,
      keepEmptyAttributes,
      mergetagsData = {},
      children,
      context,
      idx,
    } = params;

    const block = BlockManager.getBlockByType(node.type);
    if (!block) {
      throw new Error(`${node.type} block is not registered`);
    }
    const hasDiffMobileAttributes =
      node.mobileAttributes &&
      Object.keys(node.mobileAttributes).some((key) => {
        if ((node.mobileAttributes as any)[key] === undefined) return false;
        return (
          (node.mobileAttributes as any)[key] !== (node.attributes as any)[key]
        );
      });

    const hideDesktopClassName = inlineBlockTypes.includes(block.type)
      ? HIDE_DESKTOP_INLINE_BLOCK_CLASS_NAME
      : HIDE_DESKTOP_BLOCK_CLASS_NAME;

    const hideMobileClassName = inlineBlockTypes.includes(block.type)
      ? HIDE_MOBILE_INLINE_BLOCK_CLASS_NAME
      : HIDE_MOBILE_BLOCK_CLASS_NAME;

    const cssClass: string = get(node, "attributes.css-class") || "";
    if (displayMode === "only-desktop") {
      if (node.visible === "mobile") {
        return null;
      }
      if (cssClass.includes(hideDesktopClassName)) {
        return null;
      }
      if (!cssClass.includes(hideMobileClassName)) {
        set(
          node,
          "attributes.css-class",
          classnames(cssClass, hideMobileClassName)
        );
      }
    } else if (displayMode === "only-mobile") {
      if (cssClass.includes(hideMobileClassName)) {
        return null;
      }
      if (!cssClass.includes(hideDesktopClassName)) {
        set(
          node,
          "attributes.css-class",
          classnames(cssClass, hideDesktopClassName)
        );
      }
    }

    const defaultElementAttributes = {
      ...block.defaultData.attributes,
    };

    if (defaultElementAttributes) {
      if (get(node.attributes, "padding", "").trim()) {
        delete (defaultElementAttributes as any)["padding"];
        delete (defaultElementAttributes as any)["padding-top"];
        delete (defaultElementAttributes as any)["padding-bottom"];
        delete (defaultElementAttributes as any)["padding-left"];
        delete (defaultElementAttributes as any)["padding-right"];
      }
      if (get(node.attributes, "inner-padding", "").trim()) {
        delete (defaultElementAttributes as any)["inner-padding"];
        delete (defaultElementAttributes as any)["inner-padding-top"];
        delete (defaultElementAttributes as any)["inner-padding-bottom"];
        delete (defaultElementAttributes as any)["inner-padding-left"];
        delete (defaultElementAttributes as any)["inner-padding-right"];
      }
    }

    if (displayMode === "only-mobile") {
      if (node.visible === "desktop") {
        return null;
      }
      node.attributes = mergeBlock(
        mergeBlock(defaultElementAttributes),
        node.attributes as any
      );
    } else {
      node.attributes = mergeBlock(
        defaultElementAttributes,
        node.attributes as any
      );
    }

    if (node.visible === "desktop") {
      set(
        node,
        "attributes.css-class",
        classnames(cssClass, hideMobileClassName)
      );
    } else if (node.visible === "mobile") {
      set(
        node,
        "attributes.css-class",
        classnames(cssClass, hideDesktopClassName)
      );
    }
    const isOnlyMobileDisplay = node.visible === "mobile";
    const isOnlyDesktopDisplay = node.visible === "desktop";

    const mobileContent = isOnlyDesktopDisplay
      ? null
      : block.render({
          mode,
          children,
          context,
          idx,
          node: {
            ...node,
            attributes: mergeBlock(node.attributes, node.mobileAttributes),
            mobileAttributes: undefined,
          } as never,
          keepEmptyAttributes,
          mergetagsData,
          displayMode,
        });

    const desktopContent = block.render({
      ...params,
      mode,
      children,
      context,
      idx,
      node: node as never,
      keepEmptyAttributes,
      mergetagsData,
    });

    let renderContent = desktopContent;
    if (hasDiffMobileAttributes) {
      renderContent = (
        <>
          <ResponsiveElement
            desktop={() => <>{desktopContent}</>}
            mobile={() => <>{mobileContent}</>}
          />
        </>
      );
    }

    return renderContent;
  };
}
